import * as am5 from '@amcharts/amcharts5';
import { HttpClient } from '@angular/common/http';
import { ElementRef, Injectable, inject } from '@angular/core';

import { lastValueFrom, map } from 'rxjs';
import {
    GeoJson,
    IIqTrendlinesMapStateService,
    LtGeoData,
    RateFlatbedColor,
    RateReeferColor,
    RateVanColor,
    RatesGeoData,
    RegionValues,
    TL_MAP_LT_URL,
    ratesData,
    TL_REGIONAL_RATES_URL,
    TRENDLINES_CACHE_DB,
    TRENDLINES_DC_VAN_LT_MAP_DOC,
    TRENDLINES_DC_REEFER_LT_MAP_DOC,
    TRENDLINES_DC_FLATBED_LT_MAP_DOC,
    TRENDLINES_FLATBED_MAP_RATE_DOC,
    TRENDLINES_REEFER_MAP_RATE_DOC,
    TRENDLINES_VAN_MAP_RATE_DOC,
    RatesResponse,
} from '../../models';
import * as Flatbed from './lt-graph-map-flatbed';
import * as Reefer from './lt-graph-map-reefer';
import * as Van from './lt-graph-map-van';
import * as FlatbedRate from './rate-graph-map-flatbed';
import * as ReeferRate from './rate-graph-map-reefer';
import * as VanRate from './rate-graph-map-van';
import { isNil } from 'lodash';
import { DatDbClientService } from '@dat/db';
import { DateTime } from 'luxon';
import { DatiqAppConfig } from '@dat/datiq-core/models';

type LtResponse = {
    code: string;
    loads: number;
    trucks: number;
    ratio: number;
};

type CachedLtData = {
    data: LtResponse[];
    expiryDate: string;
};

type CachedRatesData = {
    data: RatesResponse[];
    expiryDate: string;
};
const LT_DOC = new Map<string, string>([
    ['Flatbed', TRENDLINES_DC_FLATBED_LT_MAP_DOC],
    ['Van', TRENDLINES_DC_VAN_LT_MAP_DOC],
    ['Reefer', TRENDLINES_DC_REEFER_LT_MAP_DOC],
]);

const RATES_DOC = new Map<string, string>([
    ['Flatbed', TRENDLINES_FLATBED_MAP_RATE_DOC],
    ['Van', TRENDLINES_VAN_MAP_RATE_DOC],
    ['Reefer', TRENDLINES_REEFER_MAP_RATE_DOC],
]);

@Injectable({
    providedIn: 'root',
})
export class IqTrendlinesMapStateService
    implements IIqTrendlinesMapStateService
{
    private _ltBaseUrl: string =
        this._getAnalyticsUrl() + inject(TL_MAP_LT_URL);
    private _ratesBaseUrl: string =
        this._getAnalyticsUrl() + inject(TL_REGIONAL_RATES_URL);
    private _db = inject(DatDbClientService);

    private DB_OPTS: { dbName: string } = {
        dbName: TRENDLINES_CACHE_DB,
    };
    private PACIFIC_TIME_ZONE: string = 'America/Los_Angeles';

    constructor(
        private _http: HttpClient,
        private _appConfig: DatiqAppConfig,
    ) {
        this._db.createDBs([TRENDLINES_CACHE_DB]);
    }

    private _getAnalyticsUrl(): string {
        return this._appConfig?.coreConfig?.ANALYTICS_BASE_URL ?? '';
    }

    private _nextDay(): Date {
        //Next day at 7am
        return DateTime.utc()
            .setZone(this.PACIFIC_TIME_ZONE)
            .plus({ days: 1 })
            .set({
                hour: 7,
                minute: 0,
                second: 0,
                millisecond: 0,
            })
            .toJSDate();
    }

    async dataLtFlatbed(): Promise<LtGeoData[]> {
        return this._fetchLtData('Flatbed');
    }
    async dataLtVan(): Promise<LtGeoData[]> {
        return this._fetchLtData('Van');
    }
    async dataLtReefer(): Promise<LtGeoData[]> {
        return this._fetchLtData('Reefer');
    }
    async dataRateFlatbed(): Promise<RegionValues> {
        return this._fetchRateData('Flatbed');
    }
    async dataRateVan(): Promise<RegionValues> {
        return this._fetchRateData('Van');
    }
    async dataRateReefer(): Promise<RegionValues> {
        return this._fetchRateData('Reefer');
    }

    async mapRateReefer(
        container: ElementRef<any>,
        regionValues: RegionValues,
    ) {
        const west: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/west-region.data.json'),
        );
        const southwest: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/southwest.data.json'),
        );
        const southeast: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/southeast.data.json'),
        );
        const midwest: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/midwest.data.json'),
        );
        const northeast: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/northeast.data.json'),
        );
        const westData: RatesGeoData[] = ratesData(west, RateReeferColor);
        const southWestData: RatesGeoData[] = ratesData(
            southwest,
            RateReeferColor,
        );
        const southeastData: RatesGeoData[] = ratesData(
            southeast,
            RateReeferColor,
        );
        const midwestData: RatesGeoData[] = ratesData(midwest, RateReeferColor);
        const northeastData: RatesGeoData[] = ratesData(
            northeast,
            RateReeferColor,
        );

        ReeferRate.renderMap(
            container,
            { west, southwest, southeast, midwest, northeast },
            {
                west: westData,
                southwest: southWestData,
                southeast: southeastData,
                midwest: midwestData,
                northeast: northeastData,
            },
            regionValues,
        );
    }
    async mapRateVan(container: ElementRef<any>, regionValues: RegionValues) {
        const west: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/west-region.data.json'),
        );
        const southwest: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/southwest.data.json'),
        );
        const southeast: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/southeast.data.json'),
        );
        const midwest: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/midwest.data.json'),
        );
        const northeast: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/northeast.data.json'),
        );
        const westData: RatesGeoData[] = ratesData(west, RateVanColor);
        const southWestData: RatesGeoData[] = ratesData(
            southwest,
            RateVanColor,
        );
        const southeastData: RatesGeoData[] = ratesData(
            southeast,
            RateVanColor,
        );
        const midwestData: RatesGeoData[] = ratesData(midwest, RateVanColor);
        const northeastData: RatesGeoData[] = ratesData(
            northeast,
            RateVanColor,
        );

        VanRate.renderMap(
            container,
            { west, southwest, southeast, midwest, northeast },
            {
                west: westData,
                southwest: southWestData,
                southeast: southeastData,
                midwest: midwestData,
                northeast: northeastData,
            },
            regionValues,
        );
    }
    async mapRateFlatbed(
        container: ElementRef<any>,
        regionValues: RegionValues,
    ) {
        const west: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/west-region.data.json'),
        );
        const southwest: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/southwest.data.json'),
        );
        const southeast: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/southeast.data.json'),
        );
        const midwest: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/midwest.data.json'),
        );
        const northeast: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/northeast.data.json'),
        );
        const westData: RatesGeoData[] = ratesData(west, RateFlatbedColor);
        const southWestData: RatesGeoData[] = ratesData(
            southwest,
            RateFlatbedColor,
        );
        const southeastData: RatesGeoData[] = ratesData(
            southeast,
            RateFlatbedColor,
        );
        const midwestData: RatesGeoData[] = ratesData(
            midwest,
            RateFlatbedColor,
        );
        const northeastData: RatesGeoData[] = ratesData(
            northeast,
            RateFlatbedColor,
        );

        FlatbedRate.renderMap(
            container,
            { west, southwest, southeast, midwest, northeast },
            {
                west: westData,
                southwest: southWestData,
                southeast: southeastData,
                midwest: midwestData,
                northeast: northeastData,
            },
            regionValues,
        );
    }
    async mapLtFlatbed(container: ElementRef<any>, data: LtGeoData[]) {
        const geoJson: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/state.data.json'),
        );

        Flatbed.renderMap(container, geoJson, data);
    }

    async mapLtVan(container: ElementRef<any>, data: LtGeoData[]) {
        const geoJson: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/state.data.json'),
        );

        Van.renderMap(container, geoJson, data);
    }
    async mapLtReefer(container: ElementRef<any>, data: LtGeoData[]) {
        const geoJson: GeoJson = await lastValueFrom(
            this._http.get<GeoJson>('data/trendlines/state.data.json'),
        );

        Reefer.renderMap(container, geoJson, data);
    }

    private async _fetchLtData(equipment: string): Promise<LtGeoData[]> {
        const docName: string = LT_DOC.get(equipment);
        const hasDoc: boolean = await lastValueFrom(
            this._db.hasDoc(docName, this.DB_OPTS),
        );

        if (!hasDoc) {
            const data = await this._fetchAndCacheLtData(equipment, docName);
            return this._mapLtData(data);
        }
        const cachedData: CachedLtData = (await lastValueFrom(
            this._db.getDoc(docName, this.DB_OPTS),
        )) as CachedLtData;

        if (this._isDataStale(cachedData.expiryDate)) {
            const data = await this._fetchAndCacheLtData(equipment, docName);
            return this._mapLtData(data);
        }
        return this._mapLtData(cachedData.data);
    }

    private async _fetchAndCacheLtData(
        equipment: string,
        docName: string,
    ): Promise<LtResponse[]> {
        const url: string = this._ltBaseUrl.replace('%s', equipment);
        const data = await lastValueFrom(this._http.get<LtResponse[]>(url));
        this._setLtCache(docName, data);
        return data;
    }

    private _isDataStale(expiryDate: string): boolean {
        // The Dates are stored in JS Date format, we need to convert them to Pacific Local Time
        const pacificExpiryDate: DateTime = DateTime.fromISO(
            expiryDate,
        ).setZone(this.PACIFIC_TIME_ZONE);
        if (isNil(pacificExpiryDate)) return true;
        const currentPacificTime: DateTime = DateTime.utc().setZone(
            this.PACIFIC_TIME_ZONE,
        );
        return currentPacificTime > pacificExpiryDate;
    }

    private _setLtCache(docName: string, data: LtResponse[]): void {
        const expiryDate: Date = this._nextDay();
        const cachedData: CachedLtData = {
            data,
            expiryDate: expiryDate.toISOString(),
        };
        this._db.setDoc(docName, cachedData, this.DB_OPTS);
    }

    private _mapLtData(data: LtResponse[]): LtGeoData[] {
        return data.map((item): LtGeoData => {
            return {
                id: item.code,
                geoCode: item.code,
                value: item.ratio ?? 0,
                valueFormatted: '',
                minFormatted: '',
                maxFormatted: '',
                fill: am5.color('#ff0000'),
                polygonSettings: {
                    fill: am5.color('#ff0000'),
                },
            };
        });
    }

    private async _fetchRateData(equipment: string): Promise<RegionValues> {
        const docName: string = RATES_DOC.get(equipment);
        const hasDoc: boolean = await lastValueFrom(
            this._db.hasDoc(docName, this.DB_OPTS),
        );
        if (!hasDoc) {
            const data = await this._fetchAndCacheRateData(equipment);
            return this._mapRegionData(data);
        }
        const cachedData: CachedRatesData = (await lastValueFrom(
            this._db.getDoc(docName, this.DB_OPTS),
        )) as CachedRatesData;
        if (this._isDataStale(cachedData.expiryDate)) {
            const data = await this._fetchAndCacheRateData(equipment);
            return this._mapRegionData(data);
        }
        return this._mapRegionData(cachedData.data);
    }

    private async _fetchAndCacheRateData(
        equipment: string,
    ): Promise<RatesResponse[]> {
        const url = this._ratesBaseUrl.replace('%s', equipment.toUpperCase());
        const data = await lastValueFrom(this._http.get<RatesResponse[]>(url));
        this._setRateCache(RATES_DOC.get(equipment), data);
        return data;
    }

    private _setRateCache(docName: string, data: RatesResponse[]): void {
        const expiryDate: Date = this._nextDay();
        const cachedData: CachedRatesData = {
            data,
            expiryDate: expiryDate.toISOString(),
        };
        this._db.setDoc(docName, cachedData, this.DB_OPTS);
    }

    private _mapRegionData(rateItem: RatesResponse[]): RegionValues {
        const regionData: RegionValues = {
            west: 0,
            southeast: 0,
            northeast: 0,
            midwest: 0,
            southwest: 0,
        } as RegionValues;
        if (!isNil(rateItem)) {
            rateItem?.forEach((item) => {
                if (item.regionCode === 'WEST') {
                    regionData.west = item.tripRatePerMileInDollars;
                } else if (item.regionCode === 'SOUTHEAST') {
                    regionData.southeast = item.tripRatePerMileInDollars;
                } else if (item.regionCode === 'NORTHEAST') {
                    regionData.northeast = item.tripRatePerMileInDollars;
                } else if (item.regionCode === 'NORTH') {
                    regionData.midwest = item.tripRatePerMileInDollars;
                } else if (item.regionCode === 'SOUTH') {
                    regionData.southwest = item.tripRatePerMileInDollars;
                }
            });
        }
        return regionData;
    }
}
